import { FdMeasurementApplication } from 'Components/Worksheets/NuclearFieldDensityWorksheet/types';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { Guid } from '../../../../../Lib/Guid';
import { Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from '../../../../Layout';
import { WorksheetAssociatedTestSection } from '../../../Core';
import { IWorksheetComponent } from '../../../Core/types';

const Section: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const importedLaboratoryDensityId = useSelector(createWorksheetSelector!('ReferenceDensity.ImportedTest.Id')) as Guid;
  const shouldShowOtherResults = useSelector(
    pipe(
      createWorksheetSelector!('Preparation.MeasurementApplication'),
      (d) => d === FdMeasurementApplication.AbsoluteTests
    )
  );
  const stabiliserUsed = useSelector(createWorksheetSelector!('ReferenceDensityResults.StabiliserUsed')) as boolean;
  const particleDensityIsAssumed = useSelector(
    pipe(createWorksheetSelector!('IsParticleDensityAssumed'), valueOrDefault(false))
  ) as boolean;
  const lockIsParticleDensityAssumed = useSelector(
    pipe(createWorksheetSelector!('ReferenceDensityResults.ParticleDensity'), hasValue)
  );
  const confirmIsParticleDensityAssumedChange = useSelector(
    combine(
      [createWorksheetSelector!('IsParticleDensityAssumed'), createWorksheetSelector!('ParticleDensityAssumed')],
      (isParticleDensityAssumed, assumedParticleDensity) =>
        hasValue(isParticleDensityAssumed) && isParticleDensityAssumed && hasValue(assumedParticleDensity)
    )
  );

  return (
    <PageSection heading="Laboratory Maximum Density" visible={!shouldShowOtherResults}>
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="IsParticleDensityAssumed"
          options={[
            { label: 'Assumed', value: true },
            { label: 'Imported', value: false },
          ]}
          disabled={lockIsParticleDensityAssumed}
          confirm={confirmIsParticleDensityAssumedChange}
          confirmText={
            'Changing Particle Density Determination will delete the entered Assumed Particle Density. Are you sure you want to continue?'
          }
        />
        <Field input={<NumericInput />} name="ParticleDensityAssumed" visible={particleDensityIsAssumed} />
      </ListFieldSection>
      <WorksheetAssociatedTestSection
        key={`associated-test-${importedLaboratoryDensityId}`}
        label="Laboratory Density Test:"
        name="ReferenceDensity"
        testType="Maximum Dry Density"
        associatedTestModel={{
          imported: {
            testSearchLabel: 'Reference/Sample ID',
            importPropertyName: 'ReferenceDensity',
            importedTestResults: (
              <ListFieldSection>
                <Field input={<TextOutput />} label="Reference ID" name="ReferenceDensityResults.ReferenceId" />
                <Field input={<TextOutput />} label="Supplier" name="ReferenceDensityResults.Supplier" />
                <Field input={<TextOutput />} label="Source" name="ReferenceDensityResults.Source" />
                <Field input={<TextOutput />} label="Material" name="ReferenceDensityResults.Material" />
                <Field input={<TextOutput />} label="Sample Location" name="ReferenceDensityResults.SampleLocation" />
                <Field
                  input={<TextOutput />}
                  label="Stabilised Material?"
                  name="ReferenceDensityResults.StabiliserUsed"
                  format={{ type: FormatTypes.YesNo }}
                />
                <Field
                  input={<TextOutput />}
                  label="Stabiliser Type"
                  name="ReferenceDensityResults.AdditiveMaterialTypeName"
                  visible={stabiliserUsed}
                />
                <Field
                  input={<TextOutput />}
                  label="Stabiliser Amount (%)"
                  name="ReferenceDensityResults.AdditiveMaterialAmount"
                  visible={stabiliserUsed}
                  format={{ nearest: 0.1 }}
                />
                <Field
                  input={<TextOutput />}
                  label="Maximum Density Method"
                  name="ReferenceDensityResults.MaximumDensityMethod"
                />
                <Field
                  input={<TextOutput />}
                  label="Maximum Dry Density"
                  name="ReferenceDensityResults.MaximumDryDensity"
                />
                <Field
                  input={<TextOutput />}
                  label="Optimum Moisture Content (%)"
                  name="ReferenceDensityResults.OptimumMoistureContent"
                />
                <Field
                  input={<TextOutput />}
                  label="Particle Density Method"
                  name="ReferenceDensityResults.ParticleDensityMethod"
                />
                <Field input={<TextOutput />} label="Particle Density" name="ReferenceDensityResults.ParticleDensity" />
              </ListFieldSection>
            ),
            importModel: { Id: importedLaboratoryDensityId },
            queryParams: {
              limit: 50,
            },
          },
        }}
      />
    </PageSection>
  );
};

const BsLaboratoryDensitySection = withInjectedProps(Section);

export { BsLaboratoryDensitySection };
export default BsLaboratoryDensitySection;
